import { ReactElement, useMemo } from 'react';

import { Link as MiuLink } from '@mui/material';
import { Link } from '~components/index';
import { toAbsoluteUrl } from '~utils/assetsHelpers';
import { PATHS } from '~constants/index';
import { useSampleTemplates, useTypePartnerCompany } from '~hooks/index';

import { CustomNoRecordsIndicatorProps } from './types';
import {
  AlternativeOptionButton,
  ButtonImage,
  ButtonSubtitleText,
  ButtonTitle,
  Container,
  OptionsContainer,
  PrimaryOptionButton,
  SubtitleText,
  TitleText,
} from './styles';

const CustomNoRecordsIndicator = ({
  indicatorType,
  handleManualLoad,
  handleImportFile,
  handleImportInt,
}: CustomNoRecordsIndicatorProps): ReactElement => {
  const { isCarrier } = useTypePartnerCompany();

  const {
    items: ordersDowloadTemplateUrl,
    customer: customerDowloadTemplateUrl,
    vehicles: vehiclesDowloadTemplateUrl,
    drivers: driversDowloadTemplateUrl,
    partnerCompanies: partnerCompaniesTemplateUrl,
  } = useSampleTemplates();

  const indicatorOptions = useMemo(() => {
    if (indicatorType === 'orders')
      return {
        title: 'pedidos',
        subtitle: 'pedido',
        actions: {
          manualLoad: PATHS.ORDERS.NEW,
          helpLink: 'https://routix.tawk.help/category/pedidos',
          templateDownload: ordersDowloadTemplateUrl,
          fileLabel: 'una hoja de cálculo',
        },
      };

    if (indicatorType === 'customers')
      return {
        title: 'contactos o visitas frecuentes',
        subtitle: 'contacto',
        actions: {
          manualLoad: PATHS.CUSTOMERS.NEW,
          helpLink:
            'https://routix.tawk.help/article/cargar-clientes-de-forma-masiva',
          templateDownload: customerDowloadTemplateUrl,
          fileLabel: 'una hoja de cálculo',
        },
      };

    if (indicatorType === 'vehicles')
      return {
        title: 'vehículos',
        subtitle: 'vehículo',
        actions: {
          manualLoad: PATHS.CONFIG.VEHICLES.NEW,
          helpLink: 'https://routix.tawk.help', // @TODO: Verify and change
          templateDownload: vehiclesDowloadTemplateUrl,
          fileLabel: 'un CSV',
        },
      };

    if (indicatorType === 'drivers')
      return {
        title: 'choferes',
        subtitle: 'chofer',
        actions: {
          manualLoad: PATHS.CONFIG.DRIVERS.NEW,
          helpLink: 'https://routix.tawk.help', // @TODO: Verify and change
          templateDownload: driversDowloadTemplateUrl,
          fileLabel: 'un CSV',
        },
      };

    if (indicatorType === 'partnerCompanies')
      return {
        title: 'Dadores de carga asociados',
        subtitle: 'dador de carga',
        actions: {
          manualLoad: '',
          helpLink: 'https://routix.tawk.help', // @TODO: Verify and change
          templateDownload: partnerCompaniesTemplateUrl,
          fileLabel: 'una hoja de cálculo',
        },
      };
  }, [
    indicatorType,
    ordersDowloadTemplateUrl,
    customerDowloadTemplateUrl,
    vehiclesDowloadTemplateUrl,
    driversDowloadTemplateUrl,
    partnerCompaniesTemplateUrl,
  ]);

  const openTab = (url: string) => {
    window.open(url, '_blank', 'noreferrer');
  };

  return (
    <Container>
      <TitleText>{`Carga tus ${indicatorOptions?.title} a Routix`}</TitleText>
      <SubtitleText>
        Puedes{' '}
        <Link
          to={indicatorOptions?.actions.manualLoad ?? ''}
          onClick={handleManualLoad}
          underline="hover"
          fontWeight="bold"
        >
          {`crear un nuevo ${indicatorOptions?.subtitle} de forma manual`}
        </Link>
        , o importarlos masivamente.
      </SubtitleText>

      {isCarrier && (
        // @NEW-GIVER-DASHBOARD: Hide until the help URL is available for Givers
        <SubtitleText>
          {`Si necesitas ayuda sobre cómo cargar ${indicatorOptions?.subtitle}s, `}
          <MiuLink
            href={indicatorOptions?.actions.helpLink ?? ''}
            target="_blank"
            underline="hover"
            fontWeight="bold"
          >
            hace click aquí.
          </MiuLink>
        </SubtitleText>
      )}

      <OptionsContainer>
        <PrimaryOptionButton
          onClick={() =>
            openTab(indicatorOptions?.actions.templateDownload ?? '')
          }
        >
          <ButtonImage
            src={toAbsoluteUrl('/icons/icon_template.svg')}
            alt="Download Template"
          />
          <ButtonTitle>DESCARGAR PLANTILLA DE EJEMPLO</ButtonTitle>
          <ButtonSubtitleText>
            Preparamos un archivo con información de
            <br />
            ejemplo para que te sea de ayuda.
          </ButtonSubtitleText>
        </PrimaryOptionButton>

        <AlternativeOptionButton onClick={handleImportFile}>
          <ButtonImage
            src={toAbsoluteUrl('/icons/icon_import_excel.svg')}
            alt="Import File"
          />
          <ButtonTitle>CARGAR DESDE ARCHIVO</ButtonTitle>
          <ButtonSubtitleText>
            Crea o importa de a varios a la vez a través
            <br />
            {`de ${
              indicatorOptions?.actions.fileLabel ?? 'una hoja de cálculo'
            }`}
            .
          </ButtonSubtitleText>
        </AlternativeOptionButton>

        {indicatorType === 'orders' && (
          <AlternativeOptionButton onClick={handleImportInt}>
            <ButtonImage
              src={toAbsoluteUrl('/icons/icon_plug.svg')}
              alt="Import Stores"
            />
            <ButtonTitle>CARGAR DESDE TIENDAS</ButtonTitle>
            <ButtonSubtitleText>
              Importa los pedidos directamente desde tus integraciones
            </ButtonSubtitleText>
          </AlternativeOptionButton>
        )}
      </OptionsContainer>
    </Container>
  );
};

export default CustomNoRecordsIndicator;
